import { RowLimitSelector } from 'components/RowLimitSelector/RowLimitSelector'
import React, { useEffect, useRef, useState } from 'react'
import PaginatedTable from 'components/sui-react-table/components/PaginatedTable'
import ReactDOM from 'react-dom'
import { Search } from 'semantic-ui-react'

// 👇 Wrapper component that manages its own internal state
const SearchWrapper = ({ onFilter, process }) => {
  const [value, setValue] = useState('')

  useEffect(() => {
	onFilter(value)
  }, [value])

  return (
	<Search
	  placeholder={'Buscar ' + process}
	  showNoResults={false}
	  value={value}
	  onSearchChange={(e, data) => setValue(data.value || '')}
	/>
  )
}

const CustomPaginatedTable = (props) => {
	const tableRef = useRef(null)
	const insertedRef = useRef(false)
	const [rowLimit, setRowLimit] = useState(20)
	const [searchValue, setSearchValue] = useState('')
	const [filteredRows, setFilteredRows] = useState(props.rows)

	const getValueFromPath = (obj, path) => {
		return path.split('.').reduce((acc, part) => {
			if (acc && typeof acc === 'object') {
				return acc[part]
			}

			return undefined
		}, obj)
	}

	const flattenRowByColumns = (row, accessors) => {
		const values = []

		accessors.forEach((accessor) => {
			const val = getValueFromPath(row, accessor)
			
			// Si el accessor termina en '_id', buscar objeto relacionado
			if (accessor.endsWith('_id')) {
				const objKey = accessor.replace(/_id$/, '')
				// Buscar con mayúscula inicial y minúscula inicial
				const candidates = [objKey, objKey.charAt(0).toUpperCase() + objKey.slice(1)]
				let obj = null
				for (const key of candidates) {
					if (row[key] && typeof row[key] === 'object') {
						obj = row[key]
						break
					}
				}
				if (obj) {
					['name', 'descripcion'].forEach((prop) => {
						if (obj[prop] && typeof obj[prop] === 'string' && obj[prop].trim().length > 0) {
							values.push(obj[prop].trim())
						}
					})
				}
			}
			if (
				val &&
				typeof val !== 'object' &&
				(typeof val === 'string' || typeof val === 'number')
			) {
				const valType = typeof val
				const stringVal = String(val).trim()
				if (stringVal.length > 0) {
					const regex = /[A-Za-z\W_]/
					if (valType == "number" && regex.test(stringVal).length == 0) {
						return // No agregar números que no son válidos
					}
					values.push(stringVal)
				}
			}
		})

		if (Array.isArray(row.attachment)) {
			row.attachment.forEach((url) => {
				try {
					const filename = decodeURIComponent(url.split('/').pop())
					if (filename && filename.trim().length > 0) {
						values.push(filename.trim())
					}
				} catch (e) {
					// Silently ignore bad URLs
				}
			})
		}

		return values
	}

	// Filtrado en tiempo real
	useEffect(() => {
		const search = searchValue.trim().toLowerCase()
		const visibleKeys = props.columns.map((col) => col.accessor).filter(Boolean)

		const matchesSearch = (row) => {
			const values = flattenRowByColumns(row, visibleKeys)
			return values.some((val) => val.toLowerCase().includes(search))
		}

		const results = props.rows.filter(matchesSearch)
		setFilteredRows(results)
	}, [searchValue, props.rows, props.columns])

	useEffect(() => {
		const applyNotranslate = () => {
			if (tableRef.current) {
				const tds = tableRef.current.querySelectorAll('td')
				tds.forEach((td) => td.classList.add('notranslate'))

				const tfoot = tableRef.current.querySelector('tfoot')
				if (tfoot) {
					const existingSelector = tfoot.querySelector('.row-limit-container')
					if (existingSelector) {
						existingSelector.remove()
					}
				
					// Estilos del tfoot
					tfoot.style.display = 'flex'
					tfoot.style.justifyContent = 'flex-end' // todo al lado derecho
					tfoot.style.alignItems = 'center'
					tfoot.style.color = 'white'
					tfoot.style.padding = '10px'
					tfoot.style.marginRight = '90px'
					tfoot.style.overflowX = 'auto' // permite desplazamiento horizontal si se requiere
					tfoot.style.flexWrap = 'nowrap'
					tfoot.style.gap = '10px'
				
					const tfootRect = tfoot.getBoundingClientRect()
					const tfootBottom = tfootRect.bottom
					// Crear contenedor del selector
					const selectorContainer = document.createElement('div')
					selectorContainer.className = 'row-limit-container'
					selectorContainer.style.display = 'flex'
					selectorContainer.style.alignItems = 'center'
					selectorContainer.style.minWidth = '205px'
					selectorContainer.style.marginRight = '100px'
					selectorContainer.style.position = 'fixed' // Cambiado de 'absolute' a 'fixed'
					selectorContainer.style.right = '-175px' // Ajustado para mejor posicionamiento
					selectorContainer.style.bottom = `${window.innerHeight - tfootBottom +11}px` // Usar la posición del tfoot
					ReactDOM.render(
						<RowLimitSelector props={{ rowLimit, setRowLimit }} />,
						selectorContainer
					)
				
					// Añadir al final del tfoot (a la derecha)
					tfoot.appendChild(selectorContainer)
				
					// Ajustar paginación si existe
					const paginationDiv = tfoot.querySelector('.ui.pagination')
					if (paginationDiv) {
						paginationDiv.style.flexShrink = '1'
						paginationDiv.style.marginRight = '0'
					}
				}
			}
		}

		const timeoutId = setTimeout(applyNotranslate, 0)

		return () => clearTimeout(timeoutId)
	}, [props.rows, rowLimit, setRowLimit])

	useEffect(() => {
		if (insertedRef.current) return

		const div = document.querySelector('.left.aligned.middle.aligned.one.column.row')
		if (!div) return

		const buttonsAndLinks = div.querySelectorAll('button, a')
		const lastElement = buttonsAndLinks[buttonsAndLinks.length - 1]
		if (!lastElement) return

		const inputContainer = document.createElement('div')
		inputContainer.id = 'search-container'
		inputContainer.style.display = 'inline-block'
		inputContainer.style.verticalAlign = 'middle'
		inputContainer.style.marginLeft = '10px'

		lastElement.parentNode.insertBefore(inputContainer, lastElement.nextSibling)

		ReactDOM.render(
		<SearchWrapper process={props.process} onFilter={(val) => setSearchValue(val)} />,
		inputContainer
		)

		insertedRef.current = true

		return () => {
			const container = document.getElementById('search-container')
			if (container) {
				ReactDOM.unmountComponentAtNode(container)
				container.remove()
				insertedRef.current = false
			}
		}
	}, [])

	const customRenderCell = (row, column) => {
		return (
			<td className='notranslate'>
				{column.render ? column.render(row) : row[column.accessor]}
			</td>
		)
	}

	const customColumns = props.columns.map((column) => ({
		...column,
		Cell: (row) => customRenderCell(row, column),
	}))

	// const sortedRows = props.rows.sort(
	// 	(a, b) => new Date(b.createdAt) - new Date(a.createdAt)
	// )

	const propsObject = {
		...props,
		rowLimit,
		rows: filteredRows,
	}

	return (
		<div ref={tableRef}>
			{/* <RowLimitSelector props={{ rowLimit, setRowLimit }} /> */}

			<PaginatedTable {...propsObject} columns={customColumns}/>
		</div>
	)
}

export default CustomPaginatedTable
